import type { IssueImpactSchema, IssueSchema } from '@shape-construction/api/src/types';
import type { MessageGetter } from 'localization/types';

/**
 * The client code uses two enumerations for Impact
 * 1. IssueImpactSchema (autogenerated) corresponds to the backend data model.
 * 2. ImpactType contains an additional 'notEntered' status,
 *    which is not passed to the server. If the actual impact is null,
 *    the client represents it using a virtual 'notEntered' status.
 */
export type ImpactType = IssueImpactSchema | 'notEntered';

export type ImpactField = keyof Pick<IssueSchema, 'impact' | 'workAffected' | 'dueDate' | 'delayStart' | 'delayFinish'>;

interface ImpactProps {
  value: ImpactType;
  label: string;
  groupingOptionLabel: string;
  description: string;
}

export type ImpactRecord = Record<ImpactType, ImpactProps>;

export const getImpactRecord = (t: MessageGetter): ImpactRecord => {
  const impactItems: ImpactType[] = ['notEntered', 'noEffect', 'potentialDelay', 'liveDelay', 'completedDelay'];
  return impactItems.reduce(
    (acc, val) => ({
      ...acc,
      [val]: {
        value: val,
        label: t(`issue.detail.impact.impact.${val}.label`),
        groupingOptionLabel: t(`issue.detail.impact.impact.${val}.groupingOptionLabel`),
        description: t(`issue.detail.impact.impact.${val}.description`),
      },
    }),
    {} as ImpactRecord
  );
};

export const impactFieldsMap: Record<ImpactType, ImpactField[]> = {
  notEntered: ['impact'],
  noEffect: ['impact', 'dueDate'],
  potentialDelay: ['impact', 'workAffected', 'dueDate'],
  liveDelay: ['impact', 'workAffected', 'delayStart'],
  completedDelay: ['impact', 'workAffected', 'delayStart', 'delayFinish'],
};

/**
 * Adding a status type to this array will display it on the `edit impact` modal.
 * The virtual `notEntered` status should not exist in this collection.
 */
export const ImpactValues: IssueImpactSchema[] = ['noEffect', 'potentialDelay', 'liveDelay', 'completedDelay'];

export const impactFields = (impact: IssueImpactSchema) => impactFieldsMap[impact] || ['impact'];

/**
 * Per-impact colour classes, colocated so they can't drift apart unnoticed.
 * The two maps intentionally differ: `textImpactColors` styles standalone impact
 * icons/labels (e.g. issue cards), while `selectedImpactChipClasses` styles the
 * surface of the selected chip in the issue impact section.
 */
export const textImpactColors: Record<ImpactType, string> = {
  notEntered: 'text-icon-neutral-subtlest',
  noEffect: 'text-icon-neutral-subtle',
  completedDelay: 'text-icon-neutral',
  potentialDelay: 'text-icon-warning',
  liveDelay: 'text-icon-danger-bold',
};

export const selectedImpactChipClasses: Record<ImpactType, string> = {
  notEntered: 'border-neutral bg-neutral-subtle text-neutral-bold',
  noEffect: 'border-neutral bg-neutral-subtle text-neutral-bold',
  potentialDelay: 'border-warning bg-warning-subtlest text-warning',
  liveDelay: 'border-danger bg-danger-subtlest text-danger',
  completedDelay: 'border-accent-indigo bg-accent-indigo-subtlest text-accent-indigo',
};
